import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";
import type { OfflineOperation } from "../sync/types";

export interface ManagerAdministrationOperationContext {
  actorId: string;
  farmId: string;
  deviceId: string;
  performedAt: string;
}

export interface ManagerAdministrationOperationOptions {
  clock?: OperationClock;
  operationIdSource?: OperationIdSource;
}

const PROTECTED_PERMISSION_KEYS = new Set(["users.manage", "permissions.manage"]);

function create(
  context: ManagerAdministrationOperationContext,
  operationType: string,
  entityId: string,
  payload: Readonly<Record<string, unknown>>,
  options?: ManagerAdministrationOperationOptions,
): OfflineOperation {
  if (
    !context.actorId ||
    !context.farmId ||
    !context.deviceId ||
    !context.performedAt ||
    !entityId
  ) {
    throw new Error("MANAGER_ADMIN_OPERATION_CONTEXT_REQUIRED");
  }

  return createOfflineOperation(
    {
      operationType,
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId,
      performedAt: context.performedAt,
      payload,
    },
    {
      ...(options?.clock ? { clock: options.clock } : {}),
      ...(options?.operationIdSource ? { idSource: options.operationIdSource } : {}),
    },
  );
}

export function buildMembershipUpdateOperation(
  context: ManagerAdministrationOperationContext,
  input: {
    targetUserId: string;
    role: "owner" | "manager" | "operator";
    isActive: boolean;
  },
  options?: ManagerAdministrationOperationOptions,
): OfflineOperation {
  if (!input.targetUserId || !["owner", "manager", "operator"].includes(input.role)) {
    throw new Error("MEMBERSHIP_UPDATE_INPUT_INVALID");
  }

  return create(
    context,
    "membership.update",
    input.targetUserId,
    {
      targetUserId: input.targetUserId,
      role: input.role,
      isActive: input.isActive,
    },
    options,
  );
}

function assertOverridePermission(permissionKey: string): string {
  const normalized = permissionKey.trim();
  if (!normalized) throw new Error("PERMISSION_KEY_REQUIRED");
  if (PROTECTED_PERMISSION_KEYS.has(normalized)) {
    throw new Error("PERMISSION_OVERRIDE_PROTECTED");
  }
  return normalized;
}

export function buildPermissionOverrideSetOperation(
  context: ManagerAdministrationOperationContext,
  input: {
    targetUserId: string;
    permissionKey: string;
    decision: boolean;
  },
  options?: ManagerAdministrationOperationOptions,
): OfflineOperation {
  if (!input.targetUserId) throw new Error("PERMISSION_OVERRIDE_USER_REQUIRED");
  const permissionKey = assertOverridePermission(input.permissionKey);

  return create(
    context,
    "permission.override.set",
    input.targetUserId,
    {
      targetUserId: input.targetUserId,
      permissionKey,
      decision: input.decision,
    },
    options,
  );
}

export function buildPermissionOverrideRemoveOperation(
  context: ManagerAdministrationOperationContext,
  input: {
    targetUserId: string;
    permissionKey: string;
  },
  options?: ManagerAdministrationOperationOptions,
): OfflineOperation {
  if (!input.targetUserId) throw new Error("PERMISSION_OVERRIDE_USER_REQUIRED");
  const permissionKey = assertOverridePermission(input.permissionKey);

  return create(
    context,
    "permission.override.remove",
    input.targetUserId,
    {
      targetUserId: input.targetUserId,
      permissionKey,
    },
    options,
  );
}

export function buildDutyAssignmentOperation(
  context: ManagerAdministrationOperationContext,
  input: { workerUserId: string | null },
  options?: ManagerAdministrationOperationOptions,
): OfflineOperation {
  if (input.workerUserId !== null && !input.workerUserId) {
    throw new Error("DUTY_WORKER_INVALID");
  }

  return create(
    context,
    "duty.assign",
    context.farmId,
    { workerUserId: input.workerUserId },
    options,
  );
}
